/**
 * SenderListsPanel — the allow/block lists in Spam & AI Filter.
 *
 * The lists follow the dialog's ONE mailbox scope (the header control). Scoped
 * to a mailbox, they show that mailbox's entries and a remove affects only it;
 * on "All mailboxes" they show ONE ROW PER SENDER listing each mailbox it is in
 * (removable one by one). A second, independent "Showing" filter made the header
 * and the list disagree about what a remove touched. Each tab is searched and
 * paged server-side (a spam cleanup can leave hundreds of entries), shows its
 * count, and supports removing several entries at once. Senders can also be allowed or blocked by
 * hand; that is a user decision and replaces any existing entry, so allowing a
 * blocked sender unblocks them and vice versa.
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { EmailConnection, EmailSenderListGroup } from '@/lib/api';
import {
  useAddSpamSender,
  useRemoveSpamSenders,
  useSpamSenderPages,
  useSpamTrustSummary,
} from '../hooks/useSpam';
import { useEmailUserSettings } from '../hooks/useEmailUserSettings';

type Tab = 'blocked' | 'ai' | 'allowed';

const TABS: {
  id: Tab;
  label: string;
  disposition: 'approved' | 'rejected';
  decidedBy?: 'user' | 'automated';
  hint: string;
  empty: string;
}[] = [
  {
    id: 'blocked',
    label: 'Blocked by you',
    disposition: 'rejected',
    decidedBy: 'user',
    hint: 'Senders you reported or blocked. Their mail goes straight to Trash.',
    empty: 'You haven’t blocked any senders.',
  },
  {
    id: 'ai',
    label: 'Blocked by AI',
    disposition: 'rejected',
    decidedBy: 'automated',
    hint: 'Senders the AI filter or a failed authentication check blocked. Their mail goes to Spam.',
    empty: 'The AI filter hasn’t blocked any senders.',
  },
  {
    id: 'allowed',
    label: 'Allowed',
    disposition: 'approved',
    hint: 'Senders you allowed. Their mail always stays in your Inbox.',
    empty: 'You haven’t allowed any senders.',
  },
];

const ALL = 'all';
/** A row is one sender (grouped across mailboxes server-side). */
const rowKey = (g: EmailSenderListGroup) => g.senderAddress;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function decidedLabel(g: EmailSenderListGroup): string {
  const date = g.decidedAt ? format(new Date(g.decidedAt), 'MMM d, yyyy') : '';
  return [g.reason, date].filter(Boolean).join(' · ');
}

export function SenderListsPanel({
  accounts,
  accountId,
}: {
  accounts: EmailConnection[];
  /** The dialog's ONE mailbox scope: a mailbox, or undefined = all mailboxes.
   *  What the lists show and what remove / add affect always match it. */
  accountId: string | undefined;
}) {
  // Mailbox chips (and per-mailbox removal) only make sense when the list spans
  // several mailboxes; scoped to one, every row is that mailbox's entry.
  const multi = accounts.length > 1 && accountId === undefined;
  const [tab, setTab] = useState<Tab>('blocked');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 250) || undefined;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const addressById = useMemo(
    () => new Map(accounts.map((a) => [a.accountId, a.emailAddress])),
    [accounts],
  );

  // One query per tab so every tab header shows its count; only the active
  // tab's pages are rendered.
  const pages = {
    blocked: useSpamSenderPages({ disposition: 'rejected', decidedBy: 'user', accountId, q }),
    ai: useSpamSenderPages({ disposition: 'rejected', decidedBy: 'automated', accountId, q }),
    allowed: useSpamSenderPages({ disposition: 'approved', accountId, q }),
  };
  const active = pages[tab];
  const rows = useMemo(() => (active.data?.pages ?? []).flatMap((p) => p.groups), [active.data]);
  const total = (t: Tab) => pages[t].data?.pages[0]?.total;
  const meta = TABS.find((t) => t.id === tab)!;

  // A changed view invalidates the selection.
  useEffect(() => setSelected(new Set()), [tab, accountId, q]);

  const remove = useRemoveSpamSenders();
  // Removing a row removes the sender from EVERY mailbox it is listed in; a
  // mailbox's own × removes just that one entry.
  const removeEntries = (entries: { screenedId: string; accountId: string }[]) =>
    remove.mutate(entries, { onSuccess: () => setSelected(new Set()) });
  const selectedRows = rows.filter((g) => selected.has(rowKey(g)));
  const allShownSelected = rows.length > 0 && selectedRows.length === rows.length;

  const trust = useSpamTrustSummary(accountId, tab === 'allowed');

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Sender lists</h3>
        <span className="text-xs text-muted-foreground">
          {accountId === undefined
            ? accounts.length > 1
              ? 'All mailboxes'
              : ''
            : (accounts.find((a) => a.accountId === accountId)?.emailAddress ?? '')}
        </span>
      </div>

      <div role="tablist" aria-label="Sender lists" className="flex gap-1 border-b">
        {TABS.map((t) => {
          const n = total(t.id);
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`-mb-px border-b-2 px-2 py-1.5 text-xs font-medium ${
                tab === t.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {n !== undefined && <span className="ml-1 tabular-nums">({n.toLocaleString()})</span>}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">{meta.hint}</p>
      {tab === 'allowed' && trust.data && (trust.data.emailed > 0 || trust.data.contacts > 0) && (
        <p className="rounded-md border bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
          Also trusted automatically:{' '}
          {[
            trust.data.emailed > 0 &&
              `${trust.data.emailed.toLocaleString()} ${trust.data.emailed === 1 ? 'person' : 'people'} you’ve emailed`,
            trust.data.trustContacts &&
              trust.data.contacts > 0 &&
              `${trust.data.contacts.toLocaleString()} contact ${trust.data.contacts === 1 ? 'address' : 'addresses'}`,
          ]
            .filter(Boolean)
            .join(' · ')}
          . Their mail stays in your Inbox unless you block them.
        </p>
      )}

      {tab !== 'ai' && (
        // Keyed on the tab + header scope: a new list starts a fresh form, so a
        // mailbox picked (or an address typed) for another view never carries
        // over into one where the entry wouldn't show up.
        <AddSenderForm
          key={`${tab}:${accountId ?? 'all'}`}
          disposition={meta.disposition}
          accounts={accounts}
          defaultAccountId={accountId}
        />
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label="Search senders"
          placeholder="Search senders…"
          className="h-8 pl-7 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {active.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : rows.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">
          {q ? `No senders match “${q}”.` : meta.empty}
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 text-xs">
            <label className="flex items-center gap-1.5 text-muted-foreground">
              <input
                type="checkbox"
                aria-label="Select all shown senders"
                checked={allShownSelected}
                onChange={(e) =>
                  setSelected(e.target.checked ? new Set(rows.map(rowKey)) : new Set())
                }
              />
              {selectedRows.length > 0
                ? `${selectedRows.length} selected`
                : `${rows.length.toLocaleString()} of ${(total(tab) ?? rows.length).toLocaleString()}`}
            </label>
            {selectedRows.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs text-destructive"
                disabled={remove.isPending}
                onClick={() => removeEntries(selectedRows.flatMap((g) => g.entries))}
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove {selectedRows.length}
              </Button>
            )}
          </div>
          <ul className="space-y-1">
            {rows.map((g) => {
              const key = rowKey(g);
              const allMailboxes = g.entries.length > 1;
              return (
                <li key={key} className="flex items-start gap-2 rounded border px-2 py-1 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    aria-label={`Select ${g.senderAddress}`}
                    checked={selected.has(key)}
                    onChange={(e) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(key);
                        else next.delete(key);
                        return next;
                      })
                    }
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {g.senderName ? `${g.senderName} · ${g.senderAddress}` : g.senderAddress}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {decidedLabel(g)}
                    </span>
                    {/* The mailboxes this sender is listed in — each removable on
                        its own. Shown only when there's more than one mailbox. */}
                    {multi && (
                      <span className="mt-0.5 flex flex-wrap gap-1">
                        {g.entries.map((entry) => (
                          <span
                            key={entry.screenedId}
                            className="inline-flex items-center gap-0.5 rounded-full border bg-muted/40 py-0.5 pl-2 pr-1 text-[11px]"
                          >
                            {addressById.get(entry.accountId) ?? 'mailbox'}
                            <button
                              type="button"
                              // The icon stays small; the ::after layer grows the tap
                              // target to ~28px so it's hittable on touch screens.
                              className="relative rounded-full p-0.5 after:absolute after:-inset-2 after:content-[''] hover:bg-accent disabled:opacity-50"
                              aria-label={`Remove ${g.senderAddress} from ${
                                addressById.get(entry.accountId) ?? 'this mailbox'
                              }`}
                              disabled={remove.isPending}
                              onClick={() => removeEntries([entry])}
                            >
                              <X className="h-3 w-3" aria-hidden />
                            </button>
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    aria-label={`Remove ${g.senderAddress}${allMailboxes ? ' from all mailboxes' : ''}`}
                    title={allMailboxes ? 'Remove from all mailboxes' : 'Remove'}
                    disabled={remove.isPending}
                    onClick={() => removeEntries(g.entries)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              );
            })}
          </ul>
          {active.hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={active.isFetchingNextPage}
              onClick={() => void active.fetchNextPage()}
            >
              {active.isFetchingNextPage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Load more'
              )}
            </Button>
          )}
        </>
      )}
    </section>
  );
}

/** Allow or block an address by hand, in one mailbox or all of them. */
function AddSenderForm({
  disposition,
  accounts,
  defaultAccountId,
}: {
  disposition: 'approved' | 'rejected';
  accounts: EmailConnection[];
  defaultAccountId: string | undefined;
}) {
  const settings = useEmailUserSettings();
  const add = useAddSpamSender();
  const [address, setAddress] = useState('');
  // The default scope follows the mailbox in view and the "block in all my
  // mailboxes" preference — reactively, since settings load after mount —
  // until the user picks a scope themselves.
  const [picked, setPicked] = useState<string | null>(null);
  // Viewing all mailboxes → add to all of them (what you see is what you
  // change); viewing one → that one, unless you block across mailboxes.
  const scope =
    picked ??
    (defaultAccountId === undefined || settings.blockAcrossMailboxes ? ALL : defaultAccountId);
  const setScope = setPicked;
  const valid = /^[^\s@]+@[^\s@]+$/.test(address.trim());
  const verb = disposition === 'approved' ? 'Allow' : 'Block';
  const submit = () => {
    if (!valid) return;
    add.mutate(
      {
        senderAddress: address.trim(),
        disposition,
        ...(scope === ALL ? { allMailboxes: true } : { accountId: scope }),
      },
      { onSuccess: () => setAddress('') },
    );
  };
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Input
        aria-label={`${verb} a sender`}
        placeholder={`${verb} a sender — name@example.com`}
        className="h-8 min-w-0 flex-1 text-sm"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />
      {accounts.length > 1 && (
        <select
          aria-label={`${verb} in`}
          className="h-8 rounded-md border bg-background px-2 text-xs"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
        >
          <option value={ALL}>All mailboxes</option>
          {accounts.map((a) => (
            <option key={a.accountId} value={a.accountId}>
              {a.emailAddress}
            </option>
          ))}
        </select>
      )}
      <Button type="submit" size="sm" className="h-8" disabled={!valid || add.isPending}>
        {verb}
      </Button>
    </form>
  );
}
